import { faker } from "@faker-js/faker";
import type { Meta, StoryObj } from "@storybook/react";

import type { ListMockItemProps } from "@/__mocks__/lists";
import { fakeListMockItem } from "@/__mocks__/lists";
import { Text } from "@/ui/core";
import type { FlatListProps } from "@/ui/layout";
import { BaseCard, Box, FlatList } from "@/ui/layout";
import type { ListSeparatorType } from "@/ui/layout/list/ListComponents";
import { joinTestIDs } from "@/utils/string.util";

const ListSeparatorValues: Record<ListSeparatorType, ListSeparatorType> = {
  line: "line",
  space: "space",
};

const TestID = {
  Card: joinTestIDs("storybook", "item-card"),
  Item: joinTestIDs("storybook", "item-text"),
  List: "storybook",
};

type FlatListStoryProps<T> = Omit<
  FlatListProps<T>,
  "isInsideBottomSheet" | "listRef"
>;

const FlatListMockValues = faker.helpers.multiple(() => fakeListMockItem(), {
  count: faker.number.int({ max: 15, min: 5 }),
});

const StoryMeta: Meta<FlatListStoryProps<ListMockItemProps>> = {
  title: "Layout/Flat List",
  component: FlatList,
  argTypes: {
    isDataLoading: { control: "boolean" },
    isFABVisible: { control: "boolean" },
    isHorizontal: { control: "boolean" },
    isRefreshControlLoading: { control: "boolean" },
    onRefresh: { action: "refreshed" },
    separatorType: {
      control: "select",
      options: Object.values(ListSeparatorValues),
    },
    shouldShowRefreshControl: { control: "boolean" },
  },
  args: {
    data: FlatListMockValues,
    separatorType: "space",
    testID: TestID.List,
  },
};

type Story = StoryObj<FlatListStoryProps<ListMockItemProps>>;

const Primary: Story = {
  ...StoryMeta,
  render: (props) => (
    <Box height={props.isHorizontal ? 156 : "100%"} width="100%">
      <FlatList
        {...props}
        emptyListComponent={() => (
          <Text
            testID={joinTestIDs("storybook", "empty-list")}
            variant="bodyMedium"
          >
            No items
          </Text>
        )}
        renderItem={({ index, item }) => (
          <BaseCard
            maxWidth={props.isHorizontal ? 200 : "unset"}
            testID={joinTestIDs(TestID.Card, `${index}`)}
          >
            <Text testID={joinTestIDs(TestID.Item, `${index}`)}>
              {item.text}
            </Text>
          </BaseCard>
        )}
      />
    </Box>
  ),
};

const WithEmptyList: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    data: [],
  },
};

const Horizontal: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    isHorizontal: true,
  },
};

const WithRefreshControl: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    isRefreshControlLoading: true,
    shouldShowRefreshControl: true,
  },
};

const WithLineSeparator: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    separatorType: "line",
  },
};

const WithHeaderAndFooter: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    ListFooterComponent: () => (
      <Box backgroundColor="antiquewhite">
        <Text testID="list-footer" variant="h2">
          Footer
        </Text>
      </Box>
    ),
    ListHeaderComponent: () => (
      <Box backgroundColor="antiquewhite">
        <Text testID="list-header" variant="h2">
          Header
        </Text>
      </Box>
    ),
  },
};

export {
  Horizontal,
  Primary,
  WithEmptyList,
  WithHeaderAndFooter,
  WithLineSeparator,
  WithRefreshControl,
};
export default StoryMeta;
